import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, TrendingUp, CheckCircle, Menu, X, Search, BarChart3, Lock, Zap, MapPin } from 'lucide-react';
import GalaxyBackground from '../components/ui/GalaxyBackground';
import AnimatedSection from '../components/ui/AnimatedSection';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [showScrollTop, setShowScrollTop] = useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-[#050505] text-white overflow-x-hidden">
      {/* Background */}
      <GalaxyBackground />

      {/* Global Styles */}
      <style>{`
        :root {
          --gold: #FFD700;
          --slate: #94A3B8;
          --dark: #050505;
        }

        @keyframes glow-hover {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3), inset 0 0 20px rgba(255, 255, 255, 0.05); }
          50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.08); }
        }

        @keyframes float-3d {
          0%, 100% { transform: perspective(1000px) rotateX(5deg) rotateY(-15deg) translateZ(20px); }
          50% { transform: perspective(1000px) rotateX(5deg) rotateY(-15deg) translateZ(40px); }
        }

        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .glass-card-hover:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 215, 0, 0.3);
          box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
        }

        .gold-button {
          background: linear-gradient(135deg, #FFD700 0%, #FFC700 100%);
          color: #000;
          font-weight: 700;
          box-shadow: 0 0 30px rgba(255, 215, 0, 0.3);
          transition: all 300ms ease;
        }

        .gold-button:hover {
          box-shadow: 0 0 50px rgba(255, 215, 0, 0.6);
          transform: translateY(-2px);
        }

        .animate-float-3d {
          animation: float-3d 6s ease-in-out infinite;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
          opacity: 0;
        }

        .text-slate { color: #94A3B8; }
        .text-gold { color: #FFD700; }
      `}</style>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/5 backdrop-blur-lg glass-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-lg flex items-center justify-center text-[#050505] font-bold text-sm group-hover:scale-110 transition-transform">
              IV
            </div>
            <span className="font-['Outfit'] text-xl font-bold tracking-tight hidden sm:block">InvestVault</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-12 absolute left-1/2 transform -translate-x-1/2">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="text-slate hover:text-gold transition-colors text-sm font-medium">Features</a>
            <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="text-slate hover:text-gold transition-colors text-sm font-medium">How It Works</a>
            <a href="#assets" onClick={(e) => scrollToSection(e, 'assets')} className="text-slate hover:text-gold transition-colors text-sm font-medium">Assets</a>
            <a href="#testimonials" onClick={(e) => scrollToSection(e, 'testimonials')} className="text-slate hover:text-gold transition-colors text-sm font-medium">Testimonials</a>
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/login')} className="hidden sm:block text-slate hover:text-gold text-sm font-medium transition-colors">
              Login
            </button>
            <button onClick={() => navigate('/apply')} className="gold-button px-5 py-2 text-sm rounded-lg">
              Apply for Access
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gold"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 glass-card p-6 space-y-4">
            <a href="#features" onClick={(e) => scrollToSection(e, 'features')} className="block text-slate hover:text-gold transition-colors text-sm font-medium">Features</a>
            <a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="block text-slate hover:text-gold transition-colors text-sm font-medium">How It Works</a>
            <a href="#assets" onClick={(e) => scrollToSection(e, 'assets')} className="block text-slate hover:text-gold transition-colors text-sm font-medium">Assets</a>
            <a href="#testimonials" onClick={(e) => scrollToSection(e, 'testimonials')} className="block text-slate hover:text-gold transition-colors text-sm font-medium">Testimonials</a>
            <button onClick={() => navigate('/login')} className="block w-full text-center text-slate hover:text-gold text-sm font-medium">Login</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center py-20 px-6">
        <div className="max-w-7xl mx-auto w-full grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Content */}
          <div className="space-y-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="space-y-4">
              <h1 className="font-['Outfit'] text-6xl md:text-7xl font-bold leading-tight">
                Invest in Exclusive{' '}
                <span className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] bg-clip-text text-transparent">Real Assets</span>
                {' '}Privately
              </h1>
              <p className="text-lg text-slate leading-relaxed max-w-xl">
                Exclusive platform for approved investors to access high-quality investment opportunities with institutional-grade security and transparency.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <button onClick={() => navigate('/apply')} className="gold-button px-8 py-4 rounded-lg text-lg inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                Get Started
                <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => navigate('/login')} className="glass-card glass-card-hover px-8 py-4 rounded-lg text-lg font-bold w-full sm:w-auto">
                Investor Login
              </button>
            </div>

            {/* Trust Metrics */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10">
              <div>
                <div className="text-2xl font-bold text-gold">$84.5M</div>
                <p className="text-xs text-slate mt-1">Total AUM</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-gold">500+</div>
                <p className="text-xs text-slate mt-1">Investors</p>
              </div>
              <div>
                <div className="text-2xl font-bold text-gold">98%</div>
                <p className="text-xs text-slate mt-1">Satisfaction</p>
              </div>
            </div>
          </div>

          {/* Right: 3D Dashboard */}
          <div className="relative h-full flex items-center justify-center animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            {/* Glow behind dashboard */}
            <div
              className="absolute inset-0 blur-3xl"
              style={{
                background: 'radial-gradient(ellipse at center, rgba(255, 215, 0, 0.15) 0%, transparent 70%)',
                pointerEvents: 'none',
              }}
            ></div>

            {/* 3D Dashboard Container */}
            <div className="relative w-full max-w-md animate-float-3d">
              <div className="glass-card rounded-2xl p-6 space-y-6">
                {/* Dashboard Header */}
                <div className="flex items-center justify-between">
                  <h3 className="font-['Outfit'] text-lg font-bold">Your Portfolio</h3>
                  <div className="flex gap-2">
                    <div className="w-3 h-3 rounded-full bg-gold/50"></div>
                    <div className="w-3 h-3 rounded-full bg-gold/30"></div>
                    <div className="w-3 h-3 rounded-full bg-gold/20"></div>
                  </div>
                </div>

                {/* Stats */}
                <div className="space-y-4">
                  {/* Active Commitments */}
                  <div className="glass-card glass-card-hover rounded-lg p-4">
                    <p className="text-xs text-slate mb-1">Active Commitments</p>
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold text-gold">$450K</div>
                      <span className="text-xs text-emerald-400 font-semibold">↑ 12%</span>
                    </div>
                  </div>

                  {/* Asset Allocation */}
                  <div className="glass-card glass-card-hover rounded-lg p-4">
                    <p className="text-xs text-slate mb-2">Asset Allocation</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span>Real Estate</span>
                        <span className="text-gold font-bold">60%</span>
                      </div>
                      <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFC700] w-3/5"></div>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button className="flex-1 glass-card glass-card-hover px-3 py-2 rounded-lg text-xs font-bold text-gold border border-gold/30 hover:border-gold/60">
                      Approve
                    </button>
                    <button className="flex-1 glass-card glass-card-hover px-3 py-2 rounded-lg text-xs font-bold text-slate border border-white/10">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="relative z-10 py-20 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <AnimatedSection animation="fade-up" delay={100}>
              <div className="glass-card glass-card-hover rounded-xl p-8 h-full">
                <Search className="w-10 h-10 text-gold mb-4" />
                <h3 className="font-['Outfit'] text-xl font-bold mb-3">Explore Assets</h3>
                <p className="text-slate text-sm leading-relaxed">Browse our curated selection of premium real estate and commercial investment opportunities.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fade-up" delay={200}>
              <div className="glass-card glass-card-hover rounded-xl p-8 h-full">
                <BarChart3 className="w-10 h-10 text-gold mb-4" />
                <h3 className="font-['Outfit'] text-xl font-bold mb-3">Commit Capital</h3>
                <p className="text-slate text-sm leading-relaxed">Invest with confidence using our secure platform with transparent terms and real-time tracking.</p>
              </div>
            </AnimatedSection>
            <AnimatedSection animation="fade-up" delay={300}>
              <div className="glass-card glass-card-hover rounded-xl p-8 h-full">
                <Shield className="w-10 h-10 text-gold mb-4" />
                <h3 className="font-['Outfit'] text-xl font-bold mb-3">Track Returns</h3>
                <p className="text-slate text-sm leading-relaxed">Monitor your investments with detailed analytics and quarterly performance reports.</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-32 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Outfit'] text-5xl font-bold mb-4 text-center">How It Works</h2>
          <p className="text-center text-slate mb-20 max-w-2xl mx-auto">A smarter way to invest in real assets</p>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left: Dashboard Preview */}
            {/* Left: Dashboard Preview */}
            <div className="glass-card rounded-xl p-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="space-y-4">
                <div className="h-64 bg-gradient-to-br from-gold/10 to-transparent rounded-lg flex items-center justify-center overflow-hidden">
                   <img 
                    src="/assets/dashboard_preview.png" 
                    alt="Investment Dashboard Preview" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.style.display = 'none'; 
                      e.target.parentElement.innerHTML = '<div class="flex flex-col items-center gap-4"><svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-16 h-16 text-gold/30"><line x1="12" x2="12" y1="20" y2="10"/><line x1="18" x2="18" y1="20" y2="4"/><line x1="6" x2="6" y1="20" y2="16"/></svg><span class="text-slate text-sm">Dashboard Preview</span></div>';
                    }}
                  />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold">Investment Dashboard</h3>
                <p className="text-slate text-sm">Real-time overview of all your commitments, returns, and portfolio performance.</p>
              </div>
            </div>

            {/* Right: Steps */}
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex gap-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-gold font-['Outfit'] font-bold mb-4">
                    01
                  </div>
                  <div className="w-1 h-12 bg-gradient-to-b from-gold to-transparent"></div>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-lg font-bold mb-2">Apply for Access</h4>
                  <p className="text-slate text-sm">Submit your investment profile and get verified within 48 hours.</p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-gold font-['Outfit'] font-bold mb-4">
                    02
                  </div>
                  <div className="w-1 h-12 bg-gradient-to-b from-gold to-transparent"></div>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-lg font-bold mb-2">Browse Investments</h4>
                  <p className="text-slate text-sm">Explore opportunities with detailed analytics and expert insights.</p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full glass-card flex items-center justify-center text-gold font-['Outfit'] font-bold">
                    03
                  </div>
                </div>
                <div>
                  <h4 className="font-['Outfit'] text-lg font-bold mb-2">Commit & Track</h4>
                  <p className="text-slate text-sm">Invest, receive quarterly reports, and track returns in real-time.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Assets Section */}
      <section id="assets" className="relative z-10 py-32 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Outfit'] text-5xl font-bold mb-4 text-center">Premium Assets</h2>
          <p className="text-center text-slate mb-20 max-w-2xl mx-auto">Curated investment opportunities</p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Asset 1 */}
            <div className="glass-card glass-card-hover rounded-xl overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="h-48 bg-gradient-to-br from-gold/10 to-transparent overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&h=400&fit=crop" 
                  alt="Luxury Beach Resort"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  onError={(e) => {e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/20 to-transparent"><span class="text-6xl">🏖️</span></div>';}} 
                />
              </div>
              <div className="p-6 space-y-4">
                <h3 className="font-['Outfit'] text-xl font-bold">Luxury Beach Resort</h3>
                <p className="text-gold text-sm font-mono">Maldives • 5-Star</p>
                <p className="text-slate text-sm">Premium oceanfront with 92% occupancy and strong seasonal demand.</p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate">Expected Return</span>
                    <span className="text-gold font-bold">12.5% p.a.</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFC700] w-4/5"></div>
                  </div>
                  <p className="text-xs text-slate">87% funded</p>
                </div>
              </div>
            </div>

            {/* Asset 2 */}
            <div className="glass-card glass-card-hover rounded-xl overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="h-48 bg-gradient-to-br from-gold/10 to-transparent overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop" 
                  alt="Commercial Plaza"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  onError={(e) => {e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/20 to-transparent"><span class="text-6xl">🏢</span></div>';}} 
                />
              </div>
              <div className="p-6 space-y-4">
                <h3 className="font-['Outfit'] text-xl font-bold">Commercial Plaza</h3>
                <p className="text-gold text-sm font-mono">Dubai • Mixed-Use</p>
                <p className="text-slate text-sm">Modern hub with Fortune 500 anchor tenants and long-term leases.</p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate">Expected Return</span>
                    <span className="text-gold font-bold">10.8% p.a.</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFC700] w-1/2"></div>
                  </div>
                  <p className="text-xs text-slate">54% funded</p>
                </div>
              </div>
            </div>

            {/* Asset 3 */}
            <div className="glass-card glass-card-hover rounded-xl overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="h-48 bg-gradient-to-br from-gold/10 to-transparent overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=600&h=400&fit=crop" 
                  alt="Mixed-Use Complex Singapore"
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  onError={(e) => {e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/20 to-transparent"><span class="text-6xl">🏗️</span></div>';}} 
                />
              </div>
              <div className="p-6 space-y-4">
                <h3 className="font-['Outfit'] text-xl font-bold">Mixed-Use Complex</h3>
                <p className="text-gold text-sm font-mono">Singapore • Urban</p>
                <p className="text-slate text-sm">Contemporary development in prime location with strong growth potential.</p>
                <div className="space-y-2 pt-4 border-t border-white/10">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate">Expected Return</span>
                    <span className="text-gold font-bold">11.2% p.a.</span>
                  </div>
                  <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFC700] w-3/4"></div>
                  </div>
                  <p className="text-xs text-slate">73% funded</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="relative z-10 py-32 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-['Outfit'] text-5xl font-bold mb-4 text-center">Investor Testimonials</h2>
          <p className="text-center text-slate mb-20 max-w-2xl mx-auto">Trusted by 500+ high-net-worth investors</p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Review 1 */}
            <div className="glass-card glass-card-hover rounded-xl p-8 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="text-gold text-lg mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-slate text-sm leading-relaxed mb-6">
                "InvestVault transformed my portfolio. Exceptional transparency and professional management with returns exceeding expectations."
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFC700] flex items-center justify-center text-[#050505] font-bold text-sm">JM</div>
                <div>
                  <p className="font-['Outfit'] font-bold text-sm">James Mitchell</p>
                  <p className="text-slate text-xs">Tech Entrepreneur</p>
                </div>
              </div>
            </div>

            {/* Review 2 */}
            <div className="glass-card glass-card-hover rounded-xl p-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="text-gold text-lg mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-slate text-sm leading-relaxed mb-6">
                "Best platform for alternative investments. Thorough due diligence, excellent communication, and the interface makes tracking effortless."
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFC700] flex items-center justify-center text-[#050505] font-bold text-sm">SK</div>
                <div>
                  <p className="font-['Outfit'] font-bold text-sm">Sarah Kim</p>
                  <p className="text-slate text-xs">Private Investor</p>
                </div>
              </div>
            </div>

            {/* Review 3 */}
            <div className="glass-card glass-card-hover rounded-xl p-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="text-gold text-lg mb-4">⭐⭐⭐⭐⭐</div>
              <p className="text-slate text-sm leading-relaxed mb-6">
                "Seamless diversification into real assets. Detailed quarterly reports, consistent returns, and the team is always responsive."
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-white/10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFC700] flex items-center justify-center text-[#050505] font-bold text-sm">AR</div>
                <div>
                  <p className="font-['Outfit'] font-bold text-sm">Alexander Rhodes</p>
                  <p className="text-slate text-xs">Finance Executive</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-32 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-16 text-center border-2 border-gold/20">
            <h2 className="font-['Outfit'] text-5xl font-bold mb-6">Ready to Elevate Your Portfolio?</h2>
            <p className="text-lg text-slate mb-12 max-w-2xl mx-auto">
              Join 500+ investors accessing premium opportunities with institutional-grade security and transparency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => navigate('/apply')} className="gold-button px-8 py-4 text-lg rounded-lg inline-flex items-center justify-center gap-2">
                Apply for Access
                <ArrowRight className="w-5 h-5" />
              </button>
              <button onClick={() => navigate('/login')} className="glass-card glass-card-hover px-8 py-4 text-lg font-bold rounded-lg">
                Sign In
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-20 px-6 bg-gradient-to-b from-[#050505]/60 via-[#050505] to-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 mb-16">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-lg flex items-center justify-center text-[#050505] font-bold text-sm">IV</div>
                <span className="font-['Outfit'] text-xl font-bold">InvestVault</span>
              </div>
              <p className="text-slate text-sm leading-relaxed">
                Premium private investment platform offering secure, vetted access to real estate and commercial assets for serious investors.
              </p>
            </div>

            {/* Platform */}
            <div>
              <h4 className="font-['Outfit'] font-bold mb-4">Platform</h4>
              <ul className="space-y-2">
                <li><a href="#how-it-works" onClick={(e) => scrollToSection(e, 'how-it-works')} className="text-slate text-sm hover:text-gold transition-colors">How It Works</a></li>
                <li><a href="#assets" onClick={(e) => scrollToSection(e, 'assets')} className="text-slate text-sm hover:text-gold transition-colors">Featured Assets</a></li>
                <li><a href="#testimonials" onClick={(e) => scrollToSection(e, 'testimonials')} className="text-slate text-sm hover:text-gold transition-colors">Testimonials</a></li>
              </ul>
            </div>

            {/* Contact Us */}
            <div>
              <h4 className="font-['Outfit'] font-bold mb-4">Contact Us</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3 text-slate text-sm">
                  <MapPin className="w-5 h-5 text-gold shrink-0" />
                  <span>100 Financial District,<br />New York, NY 10005</span>
                </li>
                <li className="flex items-center gap-3 text-slate text-sm">
                  <div className="w-5 h-5 flex items-center justify-center"><span className="text-gold text-lg">@</span></div>
                  <a href="mailto:support@investvault.com" className="hover:text-gold transition-colors">support@investvault.com</a>
                </li>
                <li className="flex items-center gap-3 text-slate text-sm">
                  <div className="w-5 h-5 flex items-center justify-center"><span className="text-gold">📞</span></div>
                  <span>+1 (555) 123-4567</span>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div className="md:col-span-2">
              <h4 className="font-['Outfit'] font-bold mb-4">Stay Updated</h4>
              <p className="text-slate text-sm mb-4">
                Subscribe to our private newsletter for exclusive investment opportunities and market insights.
              </p>
              <div className="flex gap-2">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-sm text-white focus:outline-none focus:border-gold/50 flex-1"
                />
                <button className="bg-[#D4AF37] text-[#050505] font-bold px-4 py-2 rounded-lg text-sm hover:bg-[#FFC700] transition-colors">
                  Subscribe
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/10 pt-12 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-slate text-sm">&copy; 2026 InvestVault. All rights reserved.</p>
            <div className="flex gap-6 mt-4 sm:mt-0">
              <span className="text-slate text-xs">Privacy Policy</span>
              <span className="text-slate text-xs">Terms of Service</span>
            </div>
          </div>
        </div>
      </footer>
      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-24 right-8 z-50 p-4 rounded-full bg-gradient-to-br from-[#FFD700] to-[#FFC700] text-[#050505] shadow-[0_0_20px_rgba(255,215,0,0.3)] hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] transition-all duration-300 transform hover:scale-110 ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Back to Top"
      >
        <TrendingUp className="w-6 h-6" />
      </button>
    </div>
  );
}
